import * as Radix from '@radix-ui/react-context-menu';
import { Check, ChevronRight } from 'lucide-react';
import { motion } from 'motion/react';
import { useRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { springs } from '@/shared/lib/motion';
import { useMovingHighlight } from '@/shared/lib/useMovingHighlight';

import styles from '../DropdownMenu/DropdownMenu.module.scss';

/**
 * Контекстное меню по правому клику — тот же вид и та же резиновая
 * подложка, что у выпадающего меню (стили общие), только открывается в
 * точке курсора и по долгому нажатию на телефоне. Ставится лишь там, где
 * у нас есть свои действия (концепт §12): в остальных местах браузерное
 * меню — «Копировать / Вставить» люди ждут именно там.
 */
export const ContextMenu = Radix.Root;

export const ContextMenuTrigger = Radix.Trigger;

export const ContextMenuGroup = Radix.Group;

export const ContextMenuRadioGroup = Radix.RadioGroup;

export const ContextMenuSub = Radix.Sub;

export type ContextMenuContentProps = ComponentPropsWithoutRef<typeof Radix.Content>;

export function ContextMenuContent({
  className,
  collisionPadding = 12,
  children,
  ...rest
}: ContextMenuContentProps) {
  return (
    <Radix.Portal>
      <Radix.Content
        className={cx(styles['menu'], className)}
        collisionPadding={collisionPadding}
        {...rest}
      >
        <Highlighted>{children}</Highlighted>
      </Radix.Content>
    </Radix.Portal>
  );
}

function Highlighted({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const box = useMovingHighlight(ref);

  return (
    <div ref={ref} className={styles['menu__list']}>
      {box && (
        <motion.span
          className={cx(
            styles['menu__highlight'],
            box.tone === 'danger' && styles['menu__highlight--danger'],
          )}
          initial={false}
          animate={{ y: box.top, height: box.height }}
          transition={springs.rubber}
          aria-hidden="true"
        />
      )}
      {children}
    </div>
  );
}

interface ItemDecorations {
  icon?: ReactNode;
  shortcut?: string;
  tone?: 'neutral' | 'danger';
}

export type ContextMenuItemProps = ComponentPropsWithoutRef<typeof Radix.Item> &
  ItemDecorations & { children: ReactNode };

export function ContextMenuItem({
  icon,
  shortcut,
  tone = 'neutral',
  className,
  children,
  ...rest
}: ContextMenuItemProps) {
  return (
    <Radix.Item
      className={cx(
        styles['menu__item'],
        tone === 'danger' && styles['menu__item--danger'],
        className,
      )}
      data-tone={tone}
      {...rest}
    >
      {icon !== undefined && (
        <span className={styles['menu__icon']} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles['menu__label']}>{children}</span>
      {shortcut !== undefined && (
        <span className={styles['menu__shortcut']} aria-hidden="true">
          {shortcut}
        </span>
      )}
    </Radix.Item>
  );
}

export type ContextMenuRadioItemProps = ComponentPropsWithoutRef<typeof Radix.RadioItem> &
  Pick<ItemDecorations, 'icon'> & { children: ReactNode };

export function ContextMenuRadioItem({
  icon,
  className,
  children,
  ...rest
}: ContextMenuRadioItemProps) {
  return (
    <Radix.RadioItem className={cx(styles['menu__item'], className)} {...rest}>
      {icon !== undefined && (
        <span className={styles['menu__icon']} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles['menu__label']}>{children}</span>
      <span className={styles['menu__indicator']}>
        <Radix.ItemIndicator>
          <Check />
        </Radix.ItemIndicator>
      </span>
    </Radix.RadioItem>
  );
}

export type ContextMenuLabelProps = ComponentPropsWithoutRef<typeof Radix.Label>;

export function ContextMenuLabel({ className, ...rest }: ContextMenuLabelProps) {
  return <Radix.Label className={cx(styles['menu__group-label'], className)} {...rest} />;
}

export type ContextMenuSeparatorProps = ComponentPropsWithoutRef<typeof Radix.Separator>;

export function ContextMenuSeparator({ className, ...rest }: ContextMenuSeparatorProps) {
  return <Radix.Separator className={cx(styles['menu__separator'], className)} {...rest} />;
}

export type ContextMenuSubTriggerProps = ComponentPropsWithoutRef<typeof Radix.SubTrigger> &
  Pick<ItemDecorations, 'icon'> & { children: ReactNode };

export function ContextMenuSubTrigger({
  icon,
  className,
  children,
  ...rest
}: ContextMenuSubTriggerProps) {
  return (
    <Radix.SubTrigger className={cx(styles['menu__item'], className)} {...rest}>
      {icon !== undefined && (
        <span className={styles['menu__icon']} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles['menu__label']}>{children}</span>
      <span className={styles['menu__chevron']} aria-hidden="true">
        <ChevronRight size={14} />
      </span>
    </Radix.SubTrigger>
  );
}

export type ContextMenuSubContentProps = ComponentPropsWithoutRef<typeof Radix.SubContent>;

export function ContextMenuSubContent({
  className,
  sideOffset = 4,
  collisionPadding = 12,
  ...rest
}: ContextMenuSubContentProps) {
  return (
    <Radix.Portal>
      <Radix.SubContent
        className={cx(styles['menu'], className)}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        {...rest}
      />
    </Radix.Portal>
  );
}
